import React, { useEffect, useState, useMemo, useContext } from "react";
import _ from "lodash";
import styles from "./AddEditNoteDialog.module.scss";
import * as Yup from "yup";
import { useFormik, FormikProvider } from "formik";
import { getContrastText, getDeptShortName, notesSectionIsNotEmpty, recursiveMergeCustomizerWithSkipRetain } from "../../utils/misc";
import { deserialize } from "../RichText/functions";
import { FetchContext } from "../../context/fetchContext";

import {
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Button,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  useTheme,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";

import { Autocomplete } from "@material-ui/lab";
import RichText from "../RichText/RichText";
import useDidUpdateEffect from "../../hooks/useDidUpdateEffect";
import ReferenceSelector from "../ReferenceSelector/ReferenceSelector";

const StyledChip = styled(Chip)(({ pillcolor, textcolor }) => ({
  backgroundColor: pillcolor ? pillcolor : "black",
  color: textcolor ? textcolor : "white",
  "& .MuiChip-deleteIcon": {
    color: textcolor ? textcolor : "white",
  },
}));

const AddEditNoteDialog = ({
  open,
  note = {},
  orgId,
  currCorpPlanId,
  deptFilterSpid,
  handleCreate,
  handleUpdate,
  handleChangeFile,
  handleChangeText,
  handleClose,
  quarterSummary = {},
}) => {
  const theme = useTheme();

  const { requestFetch } = useContext(FetchContext);

  const [triggerRefetch, setTriggerRefetch] = useState(false);

  const { planOptions, notePlans, quarterIndex } = quarterSummary;
  const isQuarterSummary = !_.isEmpty(quarterSummary);

  const { id, text } = note;
  const isCreate = _.isNil(id);

  const getSchema = (isQuarterSummary) =>
    Yup.object().shape({
      text: Yup.array().test("notes-content-not-empty", "This field is required", function (value) {
        if (!value) return true;

        let retBoolArr = [];
        for (const section of value) {
          if (_.endsWith(section.type, "list")) {
            const listItems = _.get(section, "children", []);
            retBoolArr.push(_.some(listItems, notesSectionIsNotEmpty));
          } else {
            retBoolArr.push(notesSectionIsNotEmpty(section));
          }
        }
        return _.some(retBoolArr);
      }),
      plans: Yup.array()
        .of(Yup.string().required("Department is required"))
        .when([], {
          is: () => isQuarterSummary,
          then: (schema) => schema.min(1, "At least one department is required"),
          otherwise: (schema) => schema,
        }),
      quarter: Yup.number().when([], {
        is: () => isQuarterSummary,
        then: (schema) => schema.required(1, "At least one department is required"),
        otherwise: (schema) => schema,
      }),
      reference: Yup.object().when([], {
        is: () => !isQuarterSummary,
        then: (schema) =>
          schema.shape({
            type: Yup.string().nullable().required("Reference type is required"),
            id: Yup.string()
              .nullable()
              .when("type", {
                is: (val) => val !== "organization",
                then: (schema) => schema.required("Reference object is required"),
                otherwise: (schema) => schema,
              }),
            ids: Yup.array().of(Yup.string()).nullable(),
            context: Yup.string()
              .nullable()
              .when("type", {
                is: (val) => val === "organization",
                then: (schema) => schema.required("Reference context is required"),
                otherwise: (schema) => schema,
              }),
          }),
        otherwise: (schema) => schema,
      }),
    });

  const getInitialValues = ({ note, notePlans, quarterIndex }) => {
    let initVals = {
      text: deserialize(""),
      plans: notePlans || [],
      quarter: quarterIndex || 0,
      reference: {
        type: null,
        id: null,
        ids: null,
        context: null,
      },
    };

    if (!_.isNil(note)) {
      initVals = _.mergeWith({}, initVals, note, (objVal, srcVal, key) =>
        recursiveMergeCustomizerWithSkipRetain({ srcVal, fieldName: key, skipFields: ["reference", "referenceEntity"] })
      );
    }

    return initVals;
  };

  const initialValues = useMemo(() => {
    return getInitialValues({ note, notePlans, quarterIndex });
  }, [id, notePlans, quarterIndex]);

  const formik = useFormik({
    initialValues,
    validationSchema: getSchema(isQuarterSummary),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const { plans, quarter, reference } = values;
      const { id, ids, type, context } = reference;

      let res,
        vars = {};
      if (isCreate) {
        if (isQuarterSummary) {
          vars = {
            referenceIds: plans,
            model: "plan",
            additionalProps: { path: `periodData.${quarter}` },
          };
        } else {
          vars = { referenceId: id, referenceIds: ids, model: type };
          if (["meeting", "organization"].includes(type)) {
            vars = { ...vars, referenceId: orgId, core: context };
          } else {
            vars.additionalProps = { path: context };
          }
        }
        res = handleCreate(vars);
      } else {
        if (isQuarterSummary) {
          vars = {
            origReferenceIds: initialValues.plans,
            newReferenceIds: plans,
            model: "plan",
            origAdditionalProps: { path: `periodData.${initialValues.quarter}` },
            newAdditionalProps: { path: `periodData.${quarter}` },
          };
        }

        res = handleUpdate(vars);
      }

      if (res) requestFetch();

      setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const { values, errors, touched, dirty, handleSubmit, isSubmitting, getFieldProps, setFieldValue, setValues, resetForm } = formik;

  const handleChangeReferenceId = (item) => {
    setFieldValue("reference.id", _.get(item, "id", null));
  };

  const plansDict = useMemo(() => {
    return _.isEmpty(planOptions) ? {} : _.keyBy(planOptions, "id");
  }, [planOptions]);

  useDidUpdateEffect(() => {
    setFieldValue("text", text);
  }, [text]);

  useEffect(() => {
    if (open) {
      setTriggerRefetch((prev) => !prev);
    } else {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <div className={styles.title}>
          {isCreate ? "Add" : "Edit"} Note
          <div>
            <IconButton onClick={handleClose} size="small">
              <Close fontSize="inherit" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent style={{ overflowX: "hidden" }}>
        <FormikProvider value={formik}>
          <RichText
            value={values.text}
            readOnly={false}
            onChange={handleChangeText}
            error={Boolean(touched.text && errors.text)}
            helperText={touched.text && errors.text}
          />
          {isQuarterSummary ? (
            <div style={{ display: "flex", gap: 16, marginTop: 16, width: "calc(100% - 6px)" }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                name="plans"
                onChange={(e, plans) => setFieldValue("plans", plans)}
                options={Object.keys(plansDict)}
                value={values.plans}
                style={{ flex: 1 }}
                getOptionLabel={(plan) => _.get(plansDict, [plan, "departmentName"], "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departments"
                    name="plans"
                    variant="outlined"
                    error={Boolean(touched.plans && errors.plans)}
                    helperText={touched.plans && errors.plans}
                  />
                )}
                renderTags={(plans, getTagProps) =>
                  plans.map((plan, index) => {
                    const { departmentName, shortName, color } = _.get(plansDict, [plan], {});
                    const deptShortName = getDeptShortName(departmentName, shortName);
                    return (
                      <StyledChip
                        variant="outlined"
                        label={deptShortName}
                        {...getTagProps({ index })}
                        pillcolor={color}
                        textcolor={getContrastText(color)}
                      />
                    );
                  })
                }
              />
              <TextField
                select
                label="Quarter"
                variant="outlined"
                {...getFieldProps("quarter")}
                error={Boolean(touched.quarter && errors.quarter)}
                helperText={touched.quarter && errors.quarter}
              >
                {Array.from({ length: 4 }, (_, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {idx + 1}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          ) : (
            <div style={{ marginTop: theme.spacing(2) }}>
              <ReferenceSelector
                formik={formik}
                dialogType="note"
                refTypeCasing="camel"
                refTypeName="reference.type"
                refIdName="reference.id"
                refIdsName="reference.ids"
                refContextName="reference.context"
                refIdLabel={(refType) => `Referenced ${_.startCase(refType)}`}
                refIdsLabel={(refType) => `Referenced ${_.startCase(refType)}s`}
                triggerRefetch={triggerRefetch}
                orgId={orgId}
                currCorpPlanId={currCorpPlanId}
                deptFilterSpid={deptFilterSpid}
                handleChangeRefId={(e, newValue) => handleChangeReferenceId(newValue)}
                useRefIds={_.get(values, "reference.type") === "plan" && !_.isNil(_.get(values, "reference.context"))}
                useRefContext={_.get(values, "reference.type") === "organization"}
                disabled={!isCreate}
              />
            </div>
          )}
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <div style={{ display: "flex", gap: 16, justifyContent: "space-between", width: "100%" }}>
          <div>
            <label htmlFor="file-upload" style={{ display: "inline-block" }}>
              <Button color="primary" variant="contained" component="span">
                Upload File
              </Button>
            </label>
            <input
              name="file"
              onChange={handleChangeFile}
              accept="image/*, .doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, .pdf"
              id="file-upload"
              type="file"
              className={styles.input}
            />
            <Typography variant="subtitle2" display="inline" className={styles.filename}>
              {_.get(note, "file.name", _.get(note, "filename"))}
            </Typography>
          </div>
          <Button variant="contained" onClick={() => handleSubmit()} color="primary" disabled={isSubmitting || !dirty}>
            {_.isNil(id) ? "Create" : "Save"}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditNoteDialog;
